import { addStaticMarkerImage } from '@/lib/map/markers'

import { Severity } from './types'

export const renderMarkerSvg = (color: string, size = 24) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 2 C7.58 2 4 5.58 4 10 c0 5.25 6.5 11.5 7.1 12.07 a1 1 0 0 0 1.4 0 C13.5 21.5 20 15.25 20 10 c0-4.42-3.58-8-8-8z"
      fill={color}
      stroke="#1f2937"
      strokeWidth="1.5"
      strokeLinejoin="miter"
    />
    <circle cx="12" cy="10" r="3.2" fill="white" />
  </svg>
)

export const addSvgImage = async (map: mapboxgl.Map, name: string, svg: string, size = 24, onLoaded: () => void) => {
  if (map.hasImage(name)) return

  // 1. Load SVG into an Image
  const img = new Image()
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`

  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve()
    img.onerror = reject
  })

  // 2. Draw SVG onto a canvas
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size

  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('Canvas context not available')

  ctx.drawImage(img, 0, 0, size, size)

  // 3. Convert canvas → ImageData
  const imageData = ctx.getImageData(0, 0, size, size)

  // 4. Add raster image to Mapbox
  map.addImage(name, imageData)
  onLoaded()
}

export const getSeverityColor = (severity: string): string => {
  switch (severity) {
    case 'critical':
      return '#ef4444' // red
    case 'warning':
      return '#fbbf24' // yellow
    case 'informational':
      return '#22c55e' // green
    default:
      return '#3b82f6' // blue
  }
}

export const getSeverityFromId = (severityId: number): Severity => {
  switch (severityId) {
    case 1:
      return 'informational'
    case 2:
      return 'warning'
    case 3:
      return 'critical'
    default:
      return 'informational' // fallback
  }
}

export const severityToLevel = (severity: Severity | null) => {
  if (severity === 'critical') {
    return 2
  }
  if (severity === 'warning') {
    return 1
  }
  if (severity === 'informational') {
    return 0
  }
  return undefined
}

export const levelToSeverity = (level?: number) => {
  if (level === 2) {
    return 'critical'
  }
  if (level === 1) {
    return 'warning'
  }
  if (level === 0) {
    return 'informational'
  }
  return null
}

type Bounds = {
  north: number
  south: number
  east: number
  west: number
}

// Calculate center point from bounds
export const getCenterFromBounds = (bounds: Bounds): { latitude: number; longitude: number } => {
  return {
    latitude: (bounds.north + bounds.south) / 2,
    longitude: (bounds.east + bounds.west) / 2,
  }
}
// Calculate bounds for a given center point and zoom level
export const calculateBoundsFromZoom = (latitude: number, longitude: number, zoom: number) => {
  // At zoom level 6, the world is divided into 2^6 = 64 tiles
  // Each tile covers approximately 360 / 64 = 5.625 degrees
  // Adjust longitude span based on latitude (longitude degrees get smaller as you move away from equator)
  const longitudeSpan = 360 / Math.pow(2, zoom)
  const latitudeSpan = 360 / Math.pow(2, zoom)

  // Adjust longitude span for latitude (cosine correction)
  const adjustedLongitudeSpan = longitudeSpan / Math.cos((latitude * Math.PI) / 180)

  return {
    north: latitude + latitudeSpan / 2,
    south: latitude - latitudeSpan / 2,
    east: longitude + adjustedLongitudeSpan / 2,
    west: longitude - adjustedLongitudeSpan / 2,
  }
}

const usCenter = {
  latitude: 39.8283,
  longitude: -98.5795,
}

export const getUSBounds = (zoom: number) => {
  const bounds = calculateBoundsFromZoom(usCenter.latitude, usCenter.longitude, zoom)
  return bounds
}

// Pick an initial zoom so the full US fits regardless of screen width.
// To keep the same geographic span visible, zoom scales with log2(width).
// Anchored empirically: at ~2200px wide, zoom 4 frames the US with minimal ocean.
// Mapbox supports fractional zoom, so narrower screens (e.g. laptops) get a smooth
// partial zoom-out instead of jumping a whole integer level and hiding coastal assets.
// Wider screens get a slightly higher zoom, which also avoids showing large oceans.
export const getResponsiveUSZoom = (width?: number): number => {
  const w = width ?? (typeof window !== 'undefined' ? window.innerWidth : 2200)
  const zoom = Math.log2(w) - 7.1
  return Math.max(3, Math.min(4.5, zoom))
}

export const setCurrentPosition = (zoom: number, setBounds: (bounds: Bounds) => void, fallbackToUS: boolean) => {
  if (window?.navigator?.geolocation) {
    window.navigator.geolocation.getCurrentPosition(
      (position: GeolocationPosition) => {
        const coords = position.coords
        const bounds = calculateBoundsFromZoom(coords.latitude, coords.longitude, zoom)
        setBounds(bounds)
      },
      (error: GeolocationPositionError) => {
        console.error('Error getting user location:', error)
        if (fallbackToUS) {
          setBounds(getUSBounds(zoom))
        }
      },
      { timeout: 5000, enableHighAccuracy: false }
    )
  }
  if (fallbackToUS) {
    setBounds(getUSBounds(zoom))
  }
}

type Point = { latitude: number; longitude: number }

// Grid-based density detection. Buckets points into a lat/lng grid,
// seeds from the densest cell, and grows the region by highest-density
// neighbor until coverageTarget of all points are included. Handles
// multi-cluster data by locking onto the largest cluster.
export const findDensestRegion = (points: Point[], coverageTarget: number = 0.7, gridSize: number = 20): Point[] => {
  if (points.length <= 2) return points

  let minLat = points[0].latitude
  let maxLat = points[0].latitude
  let minLng = points[0].longitude
  let maxLng = points[0].longitude
  for (const p of points) {
    if (p.latitude < minLat) minLat = p.latitude
    if (p.latitude > maxLat) maxLat = p.latitude
    if (p.longitude < minLng) minLng = p.longitude
    if (p.longitude > maxLng) maxLng = p.longitude
  }

  const latStep = (maxLat - minLat) / gridSize || 1
  const lngStep = (maxLng - minLng) / gridSize || 1

  const grid = new Map<string, Point[]>()
  for (const p of points) {
    const row = Math.min(gridSize - 1, Math.floor((p.latitude - minLat) / latStep))
    const col = Math.min(gridSize - 1, Math.floor((p.longitude - minLng) / lngStep))
    const key = `${row},${col}`
    const bucket = grid.get(key)
    if (bucket) bucket.push(p)
    else grid.set(key, [p])
  }

  let seedKey = ''
  let seedCount = 0
  for (const [key, bucket] of grid) {
    if (bucket.length > seedCount) {
      seedCount = bucket.length
      seedKey = key
    }
  }
  if (!seedKey) return points

  const target = points.length * coverageTarget
  const selected = new Set<string>([seedKey])
  const selectedPoints: Point[] = [...(grid.get(seedKey) ?? [])]
  const neighbors: readonly [number, number][] = [
    [-1, -1],
    [-1, 0],
    [-1, 1],
    [0, -1],
    [0, 1],
    [1, -1],
    [1, 0],
    [1, 1],
  ]

  while (selectedPoints.length < target) {
    let bestKey = ''
    let bestCount = 0
    for (const key of selected) {
      const [r, c] = key.split(',').map(Number)
      for (const [dr, dc] of neighbors) {
        const nKey = `${r + dr},${c + dc}`
        if (selected.has(nKey)) continue
        const bucket = grid.get(nKey)
        if (!bucket) continue
        if (bucket.length > bestCount) {
          bestCount = bucket.length
          bestKey = nKey
        }
      }
    }
    if (!bestKey) break
    selected.add(bestKey)
    selectedPoints.push(...(grid.get(bestKey) ?? []))
  }

  return selectedPoints
}

export const zoomToCenter = (
  points: Point[],
  minZoom: number,
  maxZoom: number,
  coverageTarget: number = 0.7
): { bounds: Bounds; zoom: number } => {
  // Handle empty or single point cases
  if (points.length === 0) {
    const defaultCenter = { latitude: 39.8283, longitude: -98.5795 }
    const defaultZoom = 6
    return {
      bounds: calculateBoundsFromZoom(defaultCenter.latitude, defaultCenter.longitude, defaultZoom),
      zoom: defaultZoom,
    }
  }

  if (points.length === 1) {
    const point = points[0]
    return {
      bounds: calculateBoundsFromZoom(point.latitude, point.longitude, maxZoom),
      zoom: maxZoom,
    }
  }

  // Zero in on the densest cluster of points, ignoring outliers and minor clusters
  const filteredPoints = findDensestRegion(points, coverageTarget)

  // Calculate bounding box from filtered points
  let minLat = filteredPoints[0].latitude
  let maxLat = filteredPoints[0].latitude
  let minLng = filteredPoints[0].longitude
  let maxLng = filteredPoints[0].longitude

  for (const point of filteredPoints) {
    minLat = Math.min(minLat, point.latitude)
    maxLat = Math.max(maxLat, point.latitude)
    minLng = Math.min(minLng, point.longitude)
    maxLng = Math.max(maxLng, point.longitude)
  }

  // Calculate center point
  const centerLat = (minLat + maxLat) / 2
  const centerLng = (minLng + maxLng) / 2

  // Calculate spans with padding (10% padding on each side = 20% total)
  const latSpan = (maxLat - minLat) * 1.2
  const lngSpan = (maxLng - minLng) * 1.2

  // Handle edge case where all points are at the same location
  if (latSpan === 0 && lngSpan === 0) {
    return {
      bounds: calculateBoundsFromZoom(centerLat, centerLng, maxZoom),
      zoom: maxZoom,
    }
  }

  // Calculate required zoom level for latitude
  // At zoom z, latitude span = 360 / 2^z
  // We need: 360 / 2^z >= latSpan
  // So: 2^z <= 360 / latSpan
  // z <= log2(360 / latSpan)
  const requiredZoomForLat = Math.log2(360 / Math.max(latSpan, 0.0001))

  // Calculate required zoom level for longitude
  // At zoom z, longitude span (at centerLat) = (360 / 2^z) / cos(centerLat * π / 180)
  // We need: (360 / 2^z) / cos(centerLat * π / 180) >= lngSpan
  // So: 360 / 2^z >= lngSpan * cos(centerLat * π / 180)
  // 2^z <= 360 / (lngSpan * cos(centerLat * π / 180))
  // z <= log2(360 / (lngSpan * cos(centerLat * π / 180)))
  const cosLat = Math.cos((centerLat * Math.PI) / 180)
  const adjustedLngSpan = Math.max(lngSpan * cosLat, 0.0001)
  const requiredZoomForLng = Math.log2(360 / adjustedLngSpan)

  // Use the smaller zoom (more zoomed out) to ensure both dimensions fit
  let zoom = Math.floor(Math.min(requiredZoomForLat, requiredZoomForLng))

  // Clamp to min/max zoom
  zoom = Math.max(minZoom, Math.min(maxZoom, zoom))

  // Calculate bounds from center and zoom
  const bounds = calculateBoundsFromZoom(centerLat, centerLng, zoom)

  return { bounds, zoom }
}
/**
 * Compute bounds and zoom to fit ALL points, with optional padding.
 * Unlike zoomToCenter, this does NOT filter to a densest cluster —
 * it uses every point.
 */
export const boundsFromAllPoints = (points: Point[], padding: number = 0.1): { bounds: Bounds; zoom: number } => {
  if (points.length === 0) {
    return {
      bounds: getUSBounds(6),
      zoom: 6,
    }
  }

  if (points.length === 1) {
    return {
      bounds: calculateBoundsFromZoom(points[0].latitude, points[0].longitude, 15),
      zoom: 15,
    }
  }

  let minLat = points[0].latitude
  let maxLat = points[0].latitude
  let minLng = points[0].longitude
  let maxLng = points[0].longitude

  for (const p of points) {
    if (p.latitude < minLat) minLat = p.latitude
    if (p.latitude > maxLat) maxLat = p.latitude
    if (p.longitude < minLng) minLng = p.longitude
    if (p.longitude > maxLng) maxLng = p.longitude
  }

  const latPad = (maxLat - minLat) * padding || 0.5
  const lngPad = (maxLng - minLng) * padding || 0.5

  const bounds = {
    north: maxLat + latPad,
    south: minLat - latPad,
    east: maxLng + lngPad,
    west: minLng - lngPad,
  }

  const centerLat = (maxLat + minLat) / 2
  const lngSpan = bounds.east - bounds.west
  const latSpan = bounds.north - bounds.south
  const cosLat = Math.cos((centerLat * Math.PI) / 180)
  const zoom = Math.floor(
    Math.min(Math.log2(360 / Math.max(latSpan, 0.0001)), Math.log2(360 / Math.max(lngSpan * cosLat, 0.0001)))
  )

  return { bounds, zoom: Math.max(0, Math.min(15, zoom)) }
}

interface PulsingDot {
  width: number
  height: number
  data: Uint8Array
  context?: CanvasRenderingContext2D
  onAdd: () => void
  render: () => boolean
}

const createPulsingDot = (map: mapboxgl.Map) => {
  const pulsingDot: PulsingDot = {
    width: 100,
    height: 100,
    data: new Uint8Array(100 * 100 * 4),

    onAdd() {
      const canvas = document.createElement('canvas')
      canvas.width = this.width
      canvas.height = this.height
      this.context = canvas.getContext('2d', { willReadFrequently: true })!
    },

    render() {
      const ctx = this.context!
      const t = (performance.now() % 1000) / 1000
      const radius = 20 + t * 10

      ctx.clearRect(0, 0, this.width, this.height)
      ctx.beginPath()
      ctx.arc(50, 50, radius, 0, Math.PI * 2)
      ctx.fillStyle = `rgba(255, 144, 0, ${1 - t})`
      ctx.fill()

      this.data = new Uint8Array(ctx.getImageData(0, 0, this.width, this.height).data)
      map.triggerRepaint()
      return true
    },
  }
  return pulsingDot
}

interface AnimatedMarker {
  width: number
  height: number
  data: Uint8Array
  context?: CanvasRenderingContext2D
  onAdd(): void
  render(): boolean
}

const lerp = (a: number, b: number, t: number) => {
  return a + (b - a) * t
}

export type Coordinate = { latitude: number; longitude: number }

/**
 * Interpolates between two coordinates over a duration using requestAnimationFrame.
 * Calls onUpdate with the interpolated position on each frame.
 * Returns a cancel function to stop the animation.
 */
export const animateCoordinates = (
  from: Coordinate,
  to: Coordinate,
  durationMs: number,
  onUpdate: (coord: Coordinate) => void,
  onComplete?: () => void
): (() => void) => {
  const startTime = performance.now()
  let animationId: number | null = null

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime
    const t = Math.min(elapsed / durationMs, 1)

    // Use easeInOutCubic for smooth animation
    const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

    const interpolated: Coordinate = {
      latitude: lerp(from.latitude, to.latitude, eased),
      longitude: lerp(from.longitude, to.longitude, eased),
    }

    onUpdate(interpolated)

    if (t < 1) {
      animationId = requestAnimationFrame(animate)
    } else {
      onComplete?.()
    }
  }

  animationId = requestAnimationFrame(animate)

  return () => {
    if (animationId !== null) {
      cancelAnimationFrame(animationId)
    }
  }
}

const lerpColor = (from: [number, number, number], to: [number, number, number], t: number) => {
  const r = Math.round(lerp(from[0], to[0], t))
  const g = Math.round(lerp(from[1], to[1], t))
  const b = Math.round(lerp(from[2], to[2], t))
  return `rgb(${r}, ${g}, ${b})`
}

type RGB = [number, number, number]

export function hexToRgb(hex: string): RGB {
  let h = hex.replace('#', '').trim()
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('')
  }

  if (h.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(h)) {
    throw new Error(`Invalid hex color: ${hex}`)
  }
  const num = parseInt(h, 16)
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255]
}

const createAnimatedMarker = (map: mapboxgl.Map, color = '#ff9000'): AnimatedMarker => {
  const size = 96

  return {
    width: size,
    height: size,
    data: new Uint8Array(size * size * 4),

    onAdd() {
      const canvas = document.createElement('canvas')
      canvas.width = size
      canvas.height = size
      this.context = canvas.getContext('2d', { willReadFrequently: true })!
    },

    render() {
      const ctx = this.context!

      const t = (performance.now() % 2400) / 2400
      const pulse = t < 0.5 ? t * 2 : 2 - t * 2

      const innerColor = lerpColor(
        [255, 255, 255], // white
        //[245, 140, 0], // #ff6900
        hexToRgb(color),
        pulse
      )

      ctx.clearRect(0, 0, size, size)

      const cx = size / 2
      const cy = size / 2 + 6
      /*
      const pulseRadius = 18 + t * 12

      // --- Pulse halo (behind marker)
      ctx.beginPath()
      ctx.arc(cx, cy, pulseRadius, 0, Math.PI * 2)
      ctx.fillStyle = `rgba(255, 144, 0, ${0.35 * (1 - t)})`
      ctx.fill()
      */

      // --- Marker body (SVG-equivalent path)
      ctx.beginPath()
      ctx.moveTo(cx, cy - 28)
      ctx.bezierCurveTo(cx - 20, cy - 28, cx - 32, cy - 8, cx, cy + 30)
      ctx.bezierCurveTo(cx + 32, cy - 8, cx + 20, cy - 28, cx, cy - 28)
      ctx.closePath()

      ctx.fillStyle = color
      ctx.strokeStyle = innerColor // '#1f2937'
      //ctx.strokeStyle = '#ff6900'
      ctx.lineWidth = 4
      ctx.fill()
      ctx.stroke()

      // --- Inner white circle
      ctx.beginPath()
      ctx.arc(cx, cy - 6, 7, 0, Math.PI * 2)
      ctx.fillStyle = 'white' //innerColor // '#ff6900'
      ctx.fill()

      const imageData = ctx.getImageData(0, 0, size, size)
      this.data = new Uint8Array(imageData.data)

      map.triggerRepaint()
      return true
    },
  }
}

export const startRepaintLoop = (map: mapboxgl.Map) => {
  const repaint = () => {
    map.triggerRepaint()
    requestAnimationFrame(repaint)
  }
  repaint()
}

const images = ['critical-marker', 'default-marker', 'info-marker', 'black-marker', 'offline-marker', 'warning-marker']
const colors = [
  '#ef4444', // red
  '#FFA500', // blue
  '#22c55e', // green
  '#000000', // black
  '#999', // gray
  '#eab308', // yellow
]

export const addMarkerImages = (map: mapboxgl.Map) => {
  map.addImage('pulsing-dot', createPulsingDot(map), { pixelRatio: 2 })
  images.forEach((imageName, idx) => {
    map.addImage(`animated-${imageName}`, createAnimatedMarker(map, colors[idx]), { pixelRatio: 2 })
    void addStaticMarkerImage(map, imageName)
  })
}
